import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { expressionActions } from "@/lib/expressionActions";

export interface BackgroundStickerCreateInput {
  blob: Blob;
  filename: string;
  packName: string;
  tagsRaw: string;
  isPublic: boolean;
  audioBlob?: Blob | null;
  audioDurationMs?: number;
  audioTranscript?: string;
  clientRequestId?: string | null;
}

function makeRequestId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `sc_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

async function readErrorBody(res: Response) {
  try {
    const payload = await res.json();
    return {
      message: payload?.error || payload?.message || `Save failed (${res.status})`,
      code: payload?.code,
    };
  } catch {
    const text = await res.text().catch(() => "");
    return { message: text || `Save failed (${res.status})` };
  }
}

function buildStickerFormData(input: BackgroundStickerCreateInput, requestId: string) {
  const fd = new FormData();
  fd.append("request_id", requestId);
  fd.append("image", input.blob, input.filename || "sticker.png");
  fd.append("pack_name", input.packName.trim() || "My Pack");
  fd.append("tags", input.tagsRaw);
  fd.append("is_public", input.isPublic ? "true" : "false");

  if (input.audioBlob && input.audioBlob.size > 0) {
    fd.append("audio", input.audioBlob, "voice.webm");
    if (input.audioDurationMs && input.audioDurationMs > 0) {
      fd.append("audio_duration_ms", String(Math.round(input.audioDurationMs)));
    }
    if (input.audioTranscript && input.audioTranscript.trim()) {
      fd.append("audio_transcript", input.audioTranscript.trim());
    }
  }

  return fd;
}

async function invokeCreateStickerFallback(input: BackgroundStickerCreateInput, requestId: string) {
  const { data: payload, error } = await supabase.functions.invoke("create-sticker", {
    body: buildStickerFormData(input, requestId),
  });
  if (error) {
    return {
      ok: false as const,
      error: (payload as any)?.error || error.message || "Save failed",
      code: (payload as any)?.code || "invoke_failed",
    };
  }
  if ((payload as any)?.error) {
    return {
      ok: false as const,
      error: (payload as any).error,
      code: (payload as any).code,
    };
  }
  return { ok: true as const, payload };
}

/**
 * Fire-and-forget sticker creation that survives any UI unmount.
 *
 * The fetch lives outside React, so even if the ExpressionPanel /
 * StickerCreator unmounts (mobile keyboard dismissal, viewport resize,
 * orientation change...), the save still completes and we dispatch a
 * global event the picker listens for.
 */
export function startBackgroundStickerCreate(input: BackgroundStickerCreateInput) {
  const requestId = input.clientRequestId || makeRequestId();
  const toastId = toast.loading("Saving sticker...");
  expressionActions.stickerCreateStarted({ requestId });

  (async () => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 60000);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) {
        const msg = "Please sign in again to save stickers";
        toast.error(msg, { id: toastId });
        expressionActions.stickerCreateFailed({ error: msg, code: "missing_session", requestId });
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error("Missing VITE_SUPABASE_URL");
      }
      const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      console.log("[bg-sticker-create] posting create-sticker", {
        requestId,
        size: input.blob.size,
        filename: input.filename,
        packName: input.packName,
      });

      let res: Response;
      try {
        res = await fetch(`${supabaseUrl}/functions/v1/create-sticker`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            ...(publishableKey ? { apikey: publishableKey } : {}),
          },
          body: buildStickerFormData(input, requestId),
          signal: controller.signal,
        });
      } catch (err: any) {
        if (err?.name === "AbortError") throw err;
        console.warn("[bg-sticker-create] direct fetch failed; retrying via Supabase client", err);
        const fallback = await invokeCreateStickerFallback(input, requestId);
        if (!fallback.ok) {
          toast.error(fallback.error, { id: toastId });
          expressionActions.stickerCreateFailed({ error: fallback.error, code: fallback.code, requestId });
          return;
        }

        const inserted = (fallback.payload as any)?.sticker;
        if (!inserted?.id) {
          const msg = "Save failed: empty response";
          toast.error(msg, { id: toastId });
          expressionActions.stickerCreateFailed({ error: msg, code: "empty_response", requestId });
          return;
        }

        toast.success((fallback.payload as any)?.deduped ? "Sticker already saved" : "Sticker saved", { id: toastId });
        expressionActions.stickerCreated({ sticker: inserted, requestId });
        return;
      }

      if (!res.ok) {
        const { message, code } = await readErrorBody(res);
        console.error("[bg-sticker-create] request failed", { status: res.status, code, message });
        toast.error(message, { id: toastId });
        expressionActions.stickerCreateFailed({ error: message, code, requestId });
        return;
      }

      const payload = await res.json().catch(() => null);
      if (payload?.error) {
        const msg = payload.error;
        toast.error(msg, { id: toastId });
        expressionActions.stickerCreateFailed({ error: msg, code: payload.code, requestId });
        return;
      }

      const inserted = payload?.sticker;
      if (!inserted?.id) {
        const msg = "Save failed: empty response";
        toast.error(msg, { id: toastId });
        expressionActions.stickerCreateFailed({ error: msg, code: "empty_response", requestId });
        return;
      }

      toast.success(payload?.deduped ? "Sticker already saved" : "Sticker saved", { id: toastId });
      expressionActions.stickerCreated({ sticker: inserted, requestId });
    } catch (err: any) {
      const aborted = err?.name === "AbortError";
      const msg = aborted
        ? "Save is taking longer than expected. Tap Save again to check the same request."
        : "Save failed: " + String(err?.message || err);
      console.error("[bg-sticker-create] unexpected", err);
      toast.error(msg, { id: toastId });
      expressionActions.stickerCreateFailed({ error: msg, code: aborted ? "timeout" : "unexpected", requestId });
    } finally {
      window.clearTimeout(timeoutId);
    }
  })();

  return requestId;
}
